import React from 'react';
import { Theme } from '../Theme';
import { hasRichTextHtml, normalizeRichTextHtml } from './RichTextEditor';

type Props = {
    text: string;
    fontScale?: number;
};

type MarkdownTable = {
    headers: string[];
    rows: string[][];
    alignments: Array<React.CSSProperties['textAlign']>;
    nextLineIndex: number;
};

function parseInline(text: string, keyPrefix: string): Array<React.ReactNode> {
    const tokens: Array<React.ReactNode> = [];
    const pattern = /(\[([^\]]+)\]\(([^)]+)\))|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)|(\n)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let tokenIndex = 0;

    while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
            tokens.push(text.slice(lastIndex, match.index));
        }

        if (match[1] && match[2] && match[3]) {
            tokens.push(
                <a
                    key={`${keyPrefix}-link-${tokenIndex}`}
                    href={match[3]}
                    target='_blank'
                    rel='noreferrer'
                    style={styles.link}
                >
                    {parseInline(match[2], `${keyPrefix}-link-${tokenIndex}`)}
                </a>
            );
        } else if (match[4] && match[5]) {
            tokens.push(
                <strong key={`${keyPrefix}-strong-${tokenIndex}`}>
                    {parseInline(match[5], `${keyPrefix}-strong-${tokenIndex}`)}
                </strong>
            );
        } else if (match[6] && match[7]) {
            tokens.push(
                <em key={`${keyPrefix}-em-${tokenIndex}`}>
                    {parseInline(match[7], `${keyPrefix}-em-${tokenIndex}`)}
                </em>
            );
        } else if (match[8] && match[9]) {
            tokens.push(
                <code key={`${keyPrefix}-code-${tokenIndex}`} style={styles.code}>
                    {match[9]}
                </code>
            );
        } else if (match[10]) {
            tokens.push(<br key={`${keyPrefix}-br-${tokenIndex}`} />);
        }

        lastIndex = pattern.lastIndex;
        tokenIndex += 1;
    }

    if (lastIndex < text.length) {
        tokens.push(text.slice(lastIndex));
    }

    return tokens;
}

function getScaledFontSize(baseSize: number, fontScale: number): string {
    return `${Math.round(baseSize * fontScale * 10) / 10}px`;
}

// Treat Markdown rule-only lines as visual separators.
function isHorizontalRuleLine(line: string): boolean {
    return /^ {0,3}([-*_])(?:\s*\1){2,}\s*$/.test(line);
}

// Split simple pipe-table rows while preserving escaped pipe characters.
function splitMarkdownTableRow(line: string): string[] {
    const trimmedLine = line.trim().replace(/^\|/, '').replace(/\|$/, '');
    const cells: string[] = [];
    let currentCell = '';
    let isEscaped = false;

    for (const character of trimmedLine) {
        if (isEscaped) {
            currentCell += character;
            isEscaped = false;
            continue;
        }

        if (character === '\\') {
            isEscaped = true;
            continue;
        }

        if (character === '|') {
            cells.push(currentCell.trim());
            currentCell = '';
            continue;
        }

        currentCell += character;
    }

    cells.push(currentCell.trim());
    return cells;
}

// Markdown tables use a separator row made from dashes, optionally with alignment colons.
function getMarkdownTableSeparatorAlignments(line: string): Array<React.CSSProperties['textAlign']> | null {
    const cells = splitMarkdownTableRow(line);
    if (cells.length === 0 || !cells.every((cell) => /^:?-{3,}:?$/.test(cell.trim()))) {
        return null;
    }

    return cells.map((cell) => {
        const trimmedCell = cell.trim();
        if (trimmedCell.startsWith(':') && trimmedCell.endsWith(':')) {
            return 'center';
        }

        if (trimmedCell.endsWith(':')) {
            return 'right';
        }

        return 'left';
    });
}

// Parse a GitHub-style pipe table block starting at the current line.
function parseMarkdownTable(lines: string[], startLineIndex: number): MarkdownTable | null {
    if (startLineIndex + 1 >= lines.length || !lines[startLineIndex].includes('|')) {
        return null;
    }

    const headerCells = splitMarkdownTableRow(lines[startLineIndex]);
    const alignments = getMarkdownTableSeparatorAlignments(lines[startLineIndex + 1]);
    if (!alignments || headerCells.length === 0) {
        return null;
    }

    const rows: string[][] = [];
    let tableLineIndex = startLineIndex + 2;
    while (tableLineIndex < lines.length) {
        const currentLine = lines[tableLineIndex];
        if (!currentLine.trim() || !currentLine.includes('|')) {
            break;
        }

        rows.push(splitMarkdownTableRow(currentLine));
        tableLineIndex += 1;
    }

    return {
        headers: headerCells,
        rows,
        alignments,
        nextLineIndex: tableLineIndex,
    };
}

// Render a parsed table outside the line parser loop to avoid closing over mutable parser state.
function renderMarkdownTable(table: MarkdownTable, tableStartLineIndex: number): React.ReactNode {
    const hasVisibleHeader = table.headers.some((headerCell) => headerCell.trim());

    return (
        <div key={`table-wrap-${tableStartLineIndex}`} style={styles.tableWrapper}>
            <table style={styles.table}>
                {hasVisibleHeader && (
                    <thead>
                        <tr>
                            {table.headers.map((headerCell, cellIndex) => (
                                <th
                                    key={`table-head-${tableStartLineIndex}-${cellIndex}`}
                                    style={{
                                        ...styles.tableHeaderCell,
                                        textAlign: table.alignments[cellIndex] || 'left',
                                    }}
                                >
                                    {parseInline(headerCell, `table-head-${tableStartLineIndex}-${cellIndex}`)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                )}
                <tbody>
                    {table.rows.map((rowCells, rowIndex) => {
                        const rowKey = `table-row-${tableStartLineIndex}-${rowIndex}`;
                        return (
                            <tr key={rowKey}>
                                {Array.from({ length: Math.max(table.alignments.length, rowCells.length) })
                                    .map((_, cellIndex) => (
                                        <td
                                            key={`${rowKey}-${cellIndex}`}
                                            style={{
                                                ...styles.tableCell,
                                                textAlign: table.alignments[cellIndex] || 'left',
                                            }}
                                        >
                                            {parseInline(rowCells[cellIndex] || '', `${rowKey}-${cellIndex}`)}
                                        </td>
                                    ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export function MarkdownText({ text, fontScale = 1 }: Props) {
    // Keep report viewer zoom local by scaling only markdown-rendered text when requested.
    const containerStyle = {
        ...styles.container,
        fontSize: getScaledFontSize(14, fontScale),
    };
    const headingStyle = {
        ...styles.heading,
        fontSize: getScaledFontSize(16, fontScale),
    };

    if (hasRichTextHtml(text)) {
        // Rich-text edited reports are stored as sanitized HTML instead of Markdown.
        return (
            <div
                style={containerStyle}
                dangerouslySetInnerHTML={{ __html: normalizeRichTextHtml(text) }}
            />
        );
    }

    const lines = text.split('\n');
    const blocks: Array<React.ReactNode> = [];
    let lineIndex = 0;

    while (lineIndex < lines.length) {
        const line = lines[lineIndex];
        const trimmedLine = line.trim();

        if (!trimmedLine) {
            lineIndex += 1;
            continue;
        }

        if (isHorizontalRuleLine(line)) {
            blocks.push(<hr key={`hr-${lineIndex}`} style={styles.horizontalRule} />);
            lineIndex += 1;
            continue;
        }

        const table = parseMarkdownTable(lines, lineIndex);
        if (table) {
            blocks.push(renderMarkdownTable(table, lineIndex));
            lineIndex = table.nextLineIndex;
            continue;
        }

        const headingMatch = trimmedLine.match(/^(#{1,6})\s+(.*)$/);
        if (headingMatch) {
            const level = headingMatch[1].length;
            const Tag = `h${level}` as keyof React.JSX.IntrinsicElements;
            blocks.push(
                React.createElement(
                    Tag,
                    {
                        key: `heading-${lineIndex}`,
                        style: headingStyle,
                    },
                    parseInline(headingMatch[2], `heading-${lineIndex}`)
                )
            );
            lineIndex += 1;
            continue;
        }

        const unorderedListMatch = trimmedLine.match(/^[-*]\s+(.*)$/);
        if (unorderedListMatch) {
            const items: Array<React.ReactNode> = [];
            let listIndex = lineIndex;

            while (listIndex < lines.length) {
                const currentMatch = lines[listIndex].trim().match(/^[-*]\s+(.*)$/);
                if (!currentMatch) {
                    break;
                }

                items.push(
                    <li key={`ul-${listIndex}`} style={styles.listItem}>
                        {parseInline(currentMatch[1], `ul-${listIndex}`)}
                    </li>
                );
                listIndex += 1;
            }

            blocks.push(
                <ul key={`ul-${lineIndex}`} style={styles.list}>
                    {items}
                </ul>
            );
            lineIndex = listIndex;
            continue;
        }

        const orderedListMatch = trimmedLine.match(/^\d+\.\s+(.*)$/);
        if (orderedListMatch) {
            const items: Array<React.ReactNode> = [];
            let listIndex = lineIndex;

            while (listIndex < lines.length) {
                const currentMatch = lines[listIndex].trim().match(/^\d+\.\s+(.*)$/);
                if (!currentMatch) {
                    break;
                }

                items.push(
                    <li key={`ol-${listIndex}`} style={styles.listItem}>
                        {parseInline(currentMatch[1], `ol-${listIndex}`)}
                    </li>
                );
                listIndex += 1;
            }

            blocks.push(
                <ol key={`ol-${lineIndex}`} style={styles.list}>
                    {items}
                </ol>
            );
            lineIndex = listIndex;
            continue;
        }

        const blockquoteMatch = trimmedLine.match(/^>\s?(.*)$/);
        if (blockquoteMatch) {
            const quoteLines: string[] = [];
            let quoteIndex = lineIndex;
            const blockquoteStartIndex = lineIndex;

            while (quoteIndex < lines.length) {
                const currentLine = lines[quoteIndex].trim();
                const currentMatch = currentLine.match(/^>\s?(.*)$/);
                if (!currentMatch) {
                    break;
                }

                quoteLines.push(currentMatch[1]);
                quoteIndex += 1;
            }

            blocks.push(
                <blockquote key={`blockquote-${blockquoteStartIndex}`} style={styles.blockquote}>
                    {quoteLines.map((quoteLine, quoteLineIndex) => (
                        <p key={`blockquote-line-${blockquoteStartIndex}-${quoteLineIndex}`} style={styles.blockquoteParagraph}>
                            {parseInline(quoteLine, `blockquote-${blockquoteStartIndex}-${quoteLineIndex}`)}
                        </p>
                    ))}
                </blockquote>
            );
            lineIndex = quoteIndex;
            continue;
        }

        const paragraphLines = [line];
        let paragraphIndex = lineIndex + 1;
        while (paragraphIndex < lines.length && lines[paragraphIndex].trim()) {
            if (/^(#{1,6})\s+/.test(lines[paragraphIndex].trim())) {
                break;
            }
            if (/^[-*]\s+/.test(lines[paragraphIndex].trim())) {
                break;
            }
            if (/^\d+\.\s+/.test(lines[paragraphIndex].trim())) {
                break;
            }
            if (/^>\s?/.test(lines[paragraphIndex].trim())) {
                break;
            }
            if (isHorizontalRuleLine(lines[paragraphIndex])) {
                break;
            }
            if (parseMarkdownTable(lines, paragraphIndex)) {
                break;
            }
            paragraphLines.push(lines[paragraphIndex]);
            paragraphIndex += 1;
        }

        blocks.push(
            <p key={`p-${lineIndex}`} style={styles.paragraph}>
                {parseInline(paragraphLines.join('\n'), `p-${lineIndex}`)}
            </p>
        );
        lineIndex = paragraphIndex;
    }

    return <div style={containerStyle}>{blocks}</div>;
}

const styles: Record<string, React.CSSProperties> = {
    container: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        width: '100%',
    },
    paragraph: {
        margin: '0 0 12px 0',
        lineHeight: 1.5,
    },
    heading: {
        ...Theme.Fonts.title2,
        margin: '0 0 12px 0',
        fontWeight: 600,
    },
    blockquote: {
        margin: '0 0 12px 0',
        padding: '12px 16px',
        borderLeft: '4px solid rgba(0, 0, 0, 0.18)',
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        borderRadius: '0 8px 8px 0',
    },
    blockquoteParagraph: {
        margin: 0,
        lineHeight: 1.5,
    },
    list: {
        margin: '0 0 12px 0',
        paddingLeft: '20px',
    },
    listItem: {
        marginBottom: '6px',
        lineHeight: 1.5,
    },
    horizontalRule: {
        border: 'none',
        borderTop: Theme.Colours.border,
        margin: '18px 0',
    },
    tableWrapper: {
        width: '100%',
        overflowX: 'auto',
        margin: '0 0 16px 0',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        lineHeight: 1.45,
    },
    tableHeaderCell: {
        padding: '8px 10px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.tableHeader,
        fontWeight: 700,
        verticalAlign: 'top',
    },
    tableCell: {
        padding: '8px 10px',
        border: Theme.Colours.border,
        verticalAlign: 'top',
    },
    link: {
        color: '#2E6BE6',
        textDecoration: 'underline',
    },
    code: {
        fontFamily: 'monospace',
        fontSize: '0.95em',
        backgroundColor: 'rgba(0, 0, 0, 0.06)',
        padding: '1px 4px',
        borderRadius: '4px',
    },
};
